import { get, set } from 'lodash';

import { EnumCustomFieldDefinitionFieldType } from '../../lib/graphql';

import { type CustomFieldDef } from './CustomFieldsRenderer';

/**
 * True when a form value should count as "empty" for required-field purposes.
 * `false` (toggle off) and `0` are NOT empty; an empty array IS empty.
 * @param value
 */
function isEmptyCustomFieldValue(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

/**
 * Required toggles render as an explicit Yes/No selector (SUP-282), so an
 * unanswered toggle (`undefined`/`null`) is genuinely missing while `false`
 * ("No") is a valid answer — handled by `isEmptyCustomFieldValue`. They
 * therefore participate in required validation like any other field.
 * HangingWeight is a compound field whose own inputs already enforce required.
 * @param def
 */
function isCheckableRequiredDef(def: CustomFieldDef): boolean {
  if (!def.required) return false;
  if (def.enabled === false) return false;
  if (def.fieldType === EnumCustomFieldDefinitionFieldType.HangingWeight) {
    return false;
  }
  // Standard fields (liveWeight, hangingWeight, killDate, etc.) live at the
  // form root under their own keys, not under `customFieldValues`. The
  // per-input required validator on InputText/InputSelect already enforces
  // them, so we'd otherwise emit errors at a path no input renders — leaving
  // Save silently blocked.
  if (def.fieldSource === 'standard') return false;
  return true;
}

/**
 * Filter a definitions list down to the ones that participate in required-field
 * validation. Callers can stash the resulting IDs in form state so cheaper
 * (definition-less) validators can check them later.
 * @param definitions
 */
export function getRequiredCustomFieldIds(
  definitions: CustomFieldDef[],
): string[] {
  return definitions.filter(isCheckableRequiredDef).map((d) => d._id);
}

/**
 * Walk required custom field definitions against a values object and produce
 * Formik-shaped errors (dot-paths). `valuesAtPrefix` is the object that holds
 * `customFieldValues` directly — e.g. the form root for job-level fields, or
 * `values.head[i]` for per-head fields.
 *
 * The resulting paths are prefixed so Formik can attach them to the right
 * inputs: `${basePath ? basePath + '.' : ''}customFieldValues.${defId}`.
 * @param root0
 * @param root0.definitions
 * @param root0.valuesAtPrefix
 * @param root0.basePath
 */
export function validateRequiredCustomFields({
  definitions,
  valuesAtPrefix,
  basePath = '',
}: {
  definitions: CustomFieldDef[];
  valuesAtPrefix: unknown;
  basePath?: string;
}): Record<string, string> {
  const errors: Record<string, string> = {};
  const cfv =
    (valuesAtPrefix as { customFieldValues?: Record<string, unknown> })
      ?.customFieldValues ?? {};
  for (const def of definitions) {
    if (!isCheckableRequiredDef(def)) continue;
    if (isEmptyCustomFieldValue(cfv[def._id])) {
      const path = `${basePath ? `${basePath}.` : ''}customFieldValues.${def._id}`;
      errors[path] = `${def.label} is required`;
    }
  }
  return errors;
}

/**
 * Convenience helper that turns the flat dot-path map from
 * `validateRequiredCustomFields` into a nested errors object Formik can render
 * against `<Field>` components. Merges into an existing errors object.
 * @param flat
 * @param into
 */
export function mergeFlatErrorsIntoFormikErrors(
  flat: Record<string, string>,
  into: Record<string, unknown> = {},
): Record<string, unknown> {
  for (const [path, msg] of Object.entries(flat)) {
    set(into, path, msg);
  }
  return into;
}

/**
 * Quick check used by `canGoNext`-style gates that don't have definitions on
 * hand — pass the required-id list (stashed in form state) and the values
 * object that holds `customFieldValues`. Returns true if any required field is
 * empty.
 * @param requiredIds
 * @param valuesAtPrefix
 */
export function hasMissingRequiredCustomFields(
  requiredIds: string[] | undefined,
  valuesAtPrefix: unknown,
): boolean {
  if (!requiredIds?.length) return false;
  const cfv =
    (valuesAtPrefix as { customFieldValues?: Record<string, unknown> })
      ?.customFieldValues ?? {};
  return requiredIds.some((id) => isEmptyCustomFieldValue(cfv[id]));
}

// Re-export `get` so call sites can keep imports tidy when they want to read
// a nested path without pulling lodash directly.
export { get as getByPath };
